import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Group, Loader, Modal, MultiSelect, Stack, Switch, Table, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import {
  useDeleteSlackChannel,
  useEventTypes,
  useSaveSlackChannel,
  useSlackChannels,
  useTestSlackChannel,
  type EventTypeRow,
  type SlackChannel,
} from '@/api/ops';
import { EmptyState } from '@/components/EmptyState';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

function grouped(rows: EventTypeRow[]) {
  const families = new Map<string, string[]>();
  for (const r of rows) families.set(r.family, [...(families.get(r.family) ?? []), r.key]);
  return [...families.entries()].map(([group, items]) => ({ group, items }));
}

interface EditorProps {
  channel: SlackChannel | null;
  opened: boolean;
  onClose: () => void;
  eventTypes: EventTypeRow[];
}

function ChannelEditor({ channel, opened, onClose, eventTypes }: EditorProps) {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const save = useSaveSlackChannel();
  const [name, setName] = useState(channel?.name ?? '');
  const [webhook, setWebhook] = useState('');
  const [types, setTypes] = useState<string[]>(channel?.event_types ?? []);
  const [active, setActive] = useState(channel?.is_active ?? true);
  const data = useMemo(() => grouped(eventTypes), [eventTypes]);

  const submit = async () => {
    try {
      // An empty webhook field on an edit means "keep the stored credential" (N16).
      const body = { name, event_types: types, is_active: active, ...(webhook ? { webhook_url: webhook } : {}) };
      await save.mutateAsync(channel ? { id: channel.public_id, body } : { body });
      setWebhook('');
      onClose();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={channel ? t('slack.edit') : t('slack.add')} size="lg">
      <Stack gap="md">
        <TextInput label={t('slack.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput
          label={t('slack.webhook')}
          description={channel ? (channel.has_credential ? t('slack.webhookSaved') : t('slack.webhookMissing')) : undefined}
          value={webhook}
          autoComplete="off"
          onChange={(e) => setWebhook(e.currentTarget.value)}
        />
        <Stack gap={4}>
          <MultiSelect label={t('slack.eventTypes')} data={data} value={types} onChange={setTypes} searchable clearable />
          <Group>
            <Button size="compact-xs" variant="default" onClick={() => setTypes(eventTypes.filter((e) => e.announced_by_default).map((e) => e.key))}>
              {t('slack.defaults')}
            </Button>
          </Group>
        </Stack>
        <Switch label={t('slack.active')} checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button loading={save.isPending} onClick={() => void submit()}>
            {tc('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Slack routing for staff: which events reach which channel, and whether a channel still works. */
export function SlackChannels() {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const { dateTime } = useFormat();
  const channels = useSlackChannels();
  const eventTypes = useEventTypes();
  const save = useSaveSlackChannel();
  const test = useTestSlackChannel();
  const remove = useDeleteSlackChannel();
  const [editing, setEditing] = useState<SlackChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState<SlackChannel | null>(null);

  const sendTest = async (channel: SlackChannel) => {
    try {
      await test.mutateAsync(channel.public_id);
      notifications.show({ color: 'forest', message: t('slack.testOk') });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  const reenable = async (channel: SlackChannel) => {
    try {
      await save.mutateAsync({ id: channel.public_id, body: { is_active: true } });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.public_id);
      setToDelete(null);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  if (channels.isPending) return <Loader color="forest" />;
  if (channels.isError) return <EmptyState title={errorMessage(channels.error)} />;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={1}>{t('slack.title')}</Title>
        <Button onClick={() => setCreating(true)}>{t('slack.add')}</Button>
      </Group>

      {channels.data.filter((c) => !c.is_active && c.disabled_reason).map((c) => (
        <Alert key={c.public_id} color="red" title={`${t('slack.disabled')}: ${c.name}`}>
          <Group justify="space-between">
            <Text fz={13}>{c.disabled_reason}</Text>
            <Button size="xs" variant="default" onClick={() => void reenable(c)}>
              {t('slack.reenable')}
            </Button>
          </Group>
        </Alert>
      ))}

      {channels.data.length === 0 ? (
        <EmptyState title={t('slack.empty')} description={t('slack.emptyHint')} />
      ) : (
        <Card p={0} style={{ overflowX: 'auto' }}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('slack.name')}</Table.Th>
                <Table.Th>{t('slack.eventTypes')}</Table.Th>
                <Table.Th>{t('slack.state')}</Table.Th>
                <Table.Th>{t('slack.lastError')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {channels.data.map((c) => (
                <Table.Tr key={c.public_id}>
                  <Table.Td>
                    <Text fz={14} fw={600}>
                      {c.name}
                    </Text>
                    <Text ff="monospace" fz={11} c={brand.textMuted}>
                      {c.public_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={13}>{t('slack.events', { n: (c.event_types ?? []).length })}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={c.is_active ? 'forest' : 'red'} variant="light">
                      {c.is_active ? t('slack.stateActive') : t('slack.stateDisabled')}
                    </Badge>
                    {c.failure_count > 0 && (
                      <Text fz={11} c={brand.textMuted}>
                        {t('slack.failures', { n: c.failure_count })}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text fz={12} c={brand.textMuted}>
                      {c.last_error || '—'}
                    </Text>
                    {c.last_error_at && (
                      <Text fz={11} c={brand.textMuted}>
                        {dateTime(c.last_error_at)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <Button size="compact-xs" variant="default" onClick={() => setEditing(c)}>
                        {t('slack.edit')}
                      </Button>
                      <Button size="compact-xs" variant="default" loading={test.isPending} onClick={() => void sendTest(c)}>
                        {t('slack.test')}
                      </Button>
                      <Button size="compact-xs" color="red" variant="subtle" onClick={() => setToDelete(c)}>
                        {t('slack.delete')}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {creating && <ChannelEditor channel={null} opened onClose={() => setCreating(false)} eventTypes={eventTypes.data ?? []} />}
      {editing && <ChannelEditor key={editing.public_id} channel={editing} opened onClose={() => setEditing(null)} eventTypes={eventTypes.data ?? []} />}

      <Modal opened={!!toDelete} onClose={() => setToDelete(null)} title={t('slack.delete')}>
        <Stack gap="md">
          <Text fz={14}>{t('slack.confirmDelete', { name: toDelete?.name ?? '' })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setToDelete(null)}>
              {tc('common.cancel')}
            </Button>
            <Button color="red" loading={remove.isPending} onClick={() => void confirmDelete()}>
              {t('slack.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
